import React, { useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ItemCard } from '../ui/ItemCard';
import { CardsLayout } from '../ui/CardsLayout';
import { NewSection } from '../NewSection';
import { PremiumTitle } from '../NewSectionHeader/styled';
import { withEndlessScroll } from '../HOC/endlessScroll';
import useScreenSize from '../../hooks/useScreenSize';
import { useGPTScript } from '../../hooks/useGPTScript';
import { useFeaturedItems } from '../../hooks/useFeaturedItems';
import { useScrollTopOnTruncatedList } from '../../hooks/useScrollTopOnTruncatedList';
import { ITEMS_LIMIT } from '../../utils/constants/homePage';
import { ItemsPlaceholder } from '../Placeholders/ItemsPlaceholder';
import { endlessItemsPlaceholderCards } from '../Placeholders/EndlessItemsPlaceholder';
import { BannerPlacement } from '../../utils/constants/banners';
import { BannerSlot } from '../Banner/BannerSlot';
import { useFirstInteraction } from '../../hooks/useFirstInteraction';
import { SectionError } from '../SectionError/SectionError';

export function isBannerPosition(index: number): boolean {
  return index === 9 || (index > 9 && (index - 9) % 30 === 0);
}

const EndlessFeaturedItems = withEndlessScroll(CardsLayout);

interface Props {
  isPopState: boolean;
  isBookmarked: (id: string) => boolean;
}

export const FeaturedItemsSection: React.FC<Props> = ({
  isPopState,
  isBookmarked
}) => {
  const { t } = useTranslation();
  const { isSmall } = useScreenSize();
  const featuredListRef = useRef<HTMLDivElement>(null);
  const hasUserInteracted = useFirstInteraction();
  const { items, loading, error, onLoadMore, isStopLoad, refetch } =
    useFeaturedItems();

  useScrollTopOnTruncatedList({
    itemCount: items.length,
    firstPageSize: ITEMS_LIMIT,
    hasMorePages: !isStopLoad
  });

  const contentNodes = useMemo(
    () =>
      items.reduce<React.ReactNode[]>((acc, item, index) => {
        acc.push(
          <ItemCard
            key={item.id}
            {...item}
            cypress="featured-card-link"
            cypress-card="featured-card"
            inBookmarks={isBookmarked(item.id)}
            stat="featured-card-link"
          />
        );

        const shouldShowBanner =
          isBannerPosition(index) && !isPopState && hasUserInteracted;

        if (shouldShowBanner) {
          acc.push(
            <BannerSlot
              key={`bottom-banner-${index}`}
              placement={BannerPlacement.ContentBottom}
              overrides={{
                lotriver: {
                  containerId: `js-lotriver-bottom-banner-${index}`,
                  arCn: Number(index)
                }
              }}
            />
          );
        }

        return acc;
      }, []),
    [items, isBookmarked, isPopState, hasUserInteracted]
  );

  useGPTScript(!isPopState && items.length > 0 && isSmall && hasUserInteracted);

  const featuredItemsContent = error ? (
    <SectionError onRetry={() => refetch()} />
  ) : (
    <EndlessFeaturedItems
      items={contentNodes}
      loading={loading}
      ref={featuredListRef}
      isStopLoad={isStopLoad}
      onLoadMore={onLoadMore}
      loadingPlaceholder={endlessItemsPlaceholderCards(ITEMS_LIMIT)}
    >
      {contentNodes}
    </EndlessFeaturedItems>
  );

  return (
    <NewSection
      id="home-page-featured"
      title={t('sections.featured.title')}
      url="/items/vipped"
      urlTitle={t('sections.vipped.show_all')}
      rightBlock={
        <PremiumTitle data-cy="featured-extra-title">
          <Trans i18nKey="sections.featured.right_text" />
        </PremiumTitle>
      }
      cypress="featured-section-title"
    >
      {!(loading && !items.length) ? (
        featuredItemsContent
      ) : (
        <ItemsPlaceholder isCardsLayout noHeader />
      )}
    </NewSection>
  );
};
